import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { servicesApi, catalogTemplateApi, serviceMainCategoriesApi } from '../../services/smartBillApi';
import ProductImagePicker from '../../components/ProductImagePicker';
import './ServicesManagement.css';

const ServicesManagement = () => {
  const [searchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [mainCategoryIdFilter, setMainCategoryIdFilter] = useState(
    searchParams.get('mainCategoryId') || 'all',
  );
  const [itemKindFilter, setItemKindFilter] = useState(
    searchParams.get('itemKind') || 'all',
  );
  const [subCategoryFilter, setSubCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState(
    searchParams.get('stock') || 'all',
  );
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [selectedServices, setSelectedServices] = useState(new Set());
  const [catMain, setCatMain] = useState('general_daily_shops');
  const [catSub, setCatSub] = useState('kirana');
  const [catalogMsg, setCatalogMsg] = useState('');
  const [templateBinding, setTemplateBinding] = useState(null);
  const [templateBindingLoading, setTemplateBindingLoading] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    main_category_id: '',
    main_category: '',
    item_kind: 'service',
    default_unit: '',
    sub_category: '',
    price: '',
    gst_percent: '',
    challana: '',
    keywords: '',
    has_dynamic_sub_service: false,
    has_dynamic_price: false,
    is_active: true,
    has_challana: false,
    track_inventory: false,
    available_qty: '0',
    low_stock_threshold: '5',
    image_url: '',
    product_details: '',
  });

  const stockBadge = (service) => {
    if (!service.track_inventory) return null;
    const qty = Number(service.available_qty) || 0;
    const threshold = Number(service.low_stock_threshold) || 5;
    if (qty <= 0) return { label: 'Out', className: 'badge-danger', status: 'out' };
    if (qty <= threshold) return { label: `Low · ${qty}`, className: 'badge-warning', status: 'low' };
    return { label: `Stock · ${qty}`, className: 'badge-success', status: 'ok' };
  };

  const inventorySummary = (() => {
    const tracked = services.filter((s) => s.track_inventory);
    let low = 0;
    let out = 0;
    tracked.forEach((s) => {
      const qty = Number(s.available_qty) || 0;
      const threshold = Number(s.low_stock_threshold) || 5;
      if (qty <= 0) out += 1;
      else if (qty <= threshold) low += 1;
    });
    return { tracked: tracked.length, low, out };
  })();

  const loadMainCategories = useCallback(async () => {
    try {
      await serviceMainCategoriesApi.ensureMiscellaneous().catch(() => null);
      const res = await serviceMainCategoriesApi.getAll({ includeInactive: false });
      setMainCategories(res.data || []);
    } catch (err) {
      console.warn('Main categories load failed:', err);
      setMainCategories([]);
    }
  }, []);

  const loadTemplateBinding = useCallback(async () => {
    setTemplateBindingLoading(true);
    try {
      const res = await catalogTemplateApi.resolveMyBinding();
      const binding = res?.data || null;
      setTemplateBinding(binding);
      if (binding?.mainCategoryId) setCatMain(binding.mainCategoryId);
      if (binding?.subcategoryCode) setCatSub(binding.subcategoryCode);
    } catch {
      setTemplateBinding(null);
    } finally {
      setTemplateBindingLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplateBinding();
    loadMainCategories();
  }, [loadTemplateBinding, loadMainCategories]);

  const loadServices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const search = searchTerm.toLowerCase() === 'all' ? '' : searchTerm;
      const category = categoryFilter.toLowerCase() === 'all' ? '' : categoryFilter;
      const response = await servicesApi.getAll(search, category, showInactive, {
        mainCategoryId:
          mainCategoryIdFilter && mainCategoryIdFilter !== 'all'
            ? mainCategoryIdFilter
            : undefined,
        itemKind:
          itemKindFilter && itemKindFilter !== 'all' ? itemKindFilter : undefined,
      });
      setServices(response.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load services');
      console.error('Error loading services:', err);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, searchTerm, showInactive, mainCategoryIdFilter, itemKindFilter]);

  // Format date/time for display
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  // Get unique sub categories from services
  const getUniqueSubCategories = () => {
    const subCategories = new Set();
    services.forEach(service => {
      if (service.sub_category) {
        subCategories.add(service.sub_category);
      }
    });
    return Array.from(subCategories).sort();
  };

  // Filter and sort services
  const getFilteredAndSortedServices = () => {
    let filtered = [...services];

    // Apply sub category filter
    if (subCategoryFilter !== 'all') {
      filtered = filtered.filter(service => service.sub_category === subCategoryFilter);
    }

    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(service => service.is_active === true);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(service => service.is_active === false);
    }

    // Apply stock filter (tracked products only)
    if (stockFilter === 'tracked') {
      filtered = filtered.filter((s) => s.track_inventory);
    } else if (stockFilter === 'low') {
      filtered = filtered.filter((s) => {
        if (!s.track_inventory) return false;
        const qty = Number(s.available_qty) || 0;
        const threshold = Number(s.low_stock_threshold) || 5;
        return qty > 0 && qty <= threshold;
      });
    } else if (stockFilter === 'out') {
      filtered = filtered.filter((s) => s.track_inventory && (Number(s.available_qty) || 0) <= 0);
    }

    // Apply price range filter
    if (minPrice !== '') {
      const min = parseFloat(minPrice);
      filtered = filtered.filter(service => parseFloat(service.price || 0) >= min);
    }
    if (maxPrice !== '') {
      const max = parseFloat(maxPrice);
      filtered = filtered.filter(service => parseFloat(service.price || 0) <= max);
    }

    // Sort services
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'category':
          aValue = (a.main_category || '').toLowerCase();
          bValue = (b.main_category || '').toLowerCase();
          break;
        case 'sub_category':
          aValue = (a.sub_category || '').toLowerCase();
          bValue = (b.sub_category || '').toLowerCase();
          break;
        case 'price':
          aValue = parseFloat(a.price || 0);
          bValue = parseFloat(b.price || 0);
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0).getTime();
          bValue = new Date(b.created_at || 0).getTime();
          break;
        case 'updated_at':
          aValue = new Date(a.updated_at || a.created_at || 0).getTime();
          bValue = new Date(b.updated_at || b.created_at || 0).getTime();
          break;
        default:
          aValue = a.id;
          bValue = b.id;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const filteredServices = getFilteredAndSortedServices();

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const handleOpenModal = (service = null) => {
    if (service) {
      setEditingService(service);
      const challanaValue = service.challana || service.challana_amount || '';
      const challanaNum = parseFloat(challanaValue) || 0;
      const kind =
        service.item_kind ||
        (service.track_inventory ? 'product' : 'service');
      setFormData({
        id: service.id,
        name: service.name || '',
        main_category_id: service.main_category_id
          ? String(service.main_category_id)
          : '',
        main_category: service.main_category_name || service.main_category || '',
        item_kind: kind,
        default_unit: service.default_unit || service.main_category_unit || '',
        sub_category: service.sub_category || '',
        price: service.price || '',
        gst_percent: service.gst_percent ?? service.gstPercent ?? '',
        challana: challanaValue !== undefined && challanaValue !== null && challanaValue !== '' ? challanaValue : '0',
        keywords: Array.isArray(service.keywords) ? service.keywords.join(', ') : (service.keywords || ''),
        has_dynamic_sub_service: service.has_dynamic_sub_service || false,
        has_dynamic_price: service.has_dynamic_price || false,
        is_active: service.is_active !== undefined ? service.is_active : true,
        has_challana: challanaNum > 0 || challanaValue !== '' || challanaValue !== null,
        track_inventory: Boolean(service.track_inventory),
        available_qty: String(service.available_qty ?? 0),
        low_stock_threshold: String(service.low_stock_threshold ?? 5),
        image_url: service.image_url || service.imageUrl || service.thumbnail_url || '',
        product_details: '',
      });
    } else {
      setEditingService(null);
      const preKind = itemKindFilter !== 'all' ? itemKindFilter : 'service';
      const preCatId =
        mainCategoryIdFilter !== 'all' ? String(mainCategoryIdFilter) : '';
      const preCat = mainCategories.find((c) => String(c.id) === preCatId);
      setFormData({
        id: '',
        name: '',
        main_category_id: preCatId,
        main_category: preCat?.name || '',
        item_kind: preCat?.kind || preKind,
        default_unit: preCat?.default_unit || '',
        sub_category: '',
        price: '',
        gst_percent: '',
        challana: '0',
        keywords: '',
        has_dynamic_sub_service: false,
        has_dynamic_price: false,
        is_active: true,
        has_challana: false,
        track_inventory: (preCat?.kind || preKind) === 'product',
        available_qty: '0',
        low_stock_threshold: '5',
        image_url: '',
        product_details: '',
      });
    }
    setShowModal(true);
  };

  const handleMainCategoryChange = (e) => {
    const id = e.target.value;
    const cat = mainCategories.find((c) => String(c.id) === String(id));
    setFormData((prev) => ({
      ...prev,
      main_category_id: id,
      main_category: cat?.name || '',
      item_kind: cat?.kind || prev.item_kind,
      default_unit: cat?.default_unit || prev.default_unit,
      track_inventory:
        cat?.kind === 'product' ? true : cat?.kind === 'service' ? false : prev.track_inventory,
    }));
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingService(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const keywordsArray = formData.keywords
        ? formData.keywords.split(',').map(k => k.trim()).filter(k => k)
        : [];

      const serviceData = {
        ...formData,
        id: formData.id ? parseInt(formData.id, 10) : undefined,
        main_category_id: formData.main_category_id
          ? parseInt(formData.main_category_id, 10)
          : null,
        item_kind: formData.item_kind || 'service',
        default_unit: formData.default_unit || null,
        sub_category: formData.sub_category || null,
        price: parseFloat(formData.price) || 0,
        gst_percent: formData.gst_percent === '' || formData.gst_percent == null
          ? null
          : parseFloat(formData.gst_percent) || 0,
        challana: formData.has_challana ? (parseFloat(formData.challana) || 0) : 0,
        keywords: keywordsArray,
        track_inventory: Boolean(formData.track_inventory),
        available_qty: formData.track_inventory
          ? Math.max(0, parseInt(formData.available_qty, 10) || 0)
          : 0,
        low_stock_threshold: Math.max(
          0,
          parseInt(formData.low_stock_threshold, 10) || 5,
        ),
      };
      
      // Remove UI-only fields before sending
      delete serviceData.has_challana;
      delete serviceData.product_details;
      
      // Allow backend to auto-generate ID when left empty
      if (!serviceData.id) {
        delete serviceData.id;
      }

      if (editingService) {
        await servicesApi.update(editingService.id, serviceData);
      } else {
        await servicesApi.create(serviceData);
      }

      await loadServices();
      handleCloseModal();
    } catch (err) {
      setError(err.message || 'Failed to save service');
      console.error('Error saving service:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, hardDelete = false) => {
    if (!window.confirm(`Are you sure you want to ${hardDelete ? 'permanently delete' : 'deactivate'} this service?`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await servicesApi.delete(id, hardDelete);
      await loadServices();
      setSelectedServices(new Set());
    } catch (err) {
      setError(err.message || 'Failed to delete service');
      console.error('Error deleting service:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectService = (id) => {
    setSelectedServices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedServices.size === filteredServices.length) {
      setSelectedServices(new Set());
    } else {
      setSelectedServices(new Set(filteredServices.map(s => s.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedServices.size === 0) {
      return;
    }

    const hardDelete = window.confirm(
      `Delete ${selectedServices.size} selected service(s)?\n\nClick OK for permanent delete, Cancel for deactivate.`
    );
    
    const action = hardDelete ? 'permanently delete' : 'deactivate';
    if (!window.confirm(`⚠️ Final confirmation: ${action} ${selectedServices.size} selected service(s)?\n\nThis action cannot be undone!`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await servicesApi.deleteBulk(Array.from(selectedServices), hardDelete);
      await loadServices();
      setSelectedServices(new Set());
    } catch (err) {
      setError(err.message || 'Failed to delete selected services');
      console.error('Error deleting selected services:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    const hardDelete = window.confirm(
      `⚠️ WARNING: This will affect ALL services in the database!\n\nClick OK for permanent delete, Cancel for deactivate.`
    );
    
    const action = hardDelete ? 'permanently delete' : 'deactivate';
    const confirmMessage = `⚠️ FINAL CONFIRMATION\n\nAre you sure you want to ${action} ALL services in the database?\n\nThis action cannot be undone!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await servicesApi.deleteAll(hardDelete);
      await loadServices();
      setSelectedServices(new Set());
    } catch (err) {
      setError(err.message || 'Failed to delete all services');
      console.error('Error deleting all services:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLoadFromTemplate = async () => {
    setCatalogMsg('');
    setLoading(true);
    setError(null);
    try {
      let binding = templateBinding;
      if (!binding) {
        const bindRes = await catalogTemplateApi.resolveMyBinding();
        binding = bindRes?.data || null;
        setTemplateBinding(binding);
      }
      const businessCategoryKey = binding?.businessCategoryKey;
      const label = binding?.label || 'your shop type';

      const previewRes = businessCategoryKey
        ? await catalogTemplateApi.preview({
            categoryCode: binding.mainCategoryId,
            subcategoryCode: binding.subcategoryCode,
          })
        : null;
      const lineCount = previewRes?.data?.items?.length ?? 0;

      const confirmMsg =
        lineCount > 0
          ? `Load ${lineCount} default service line(s) for ${label}?\n\nOnly missing items will be added. Your existing services and prices stay unchanged.`
          : `Load default services for ${label}?\n\nOnly missing items will be added. No template lines found yet — ask app admin to add defaults under Catalog templates.`;

      if (!window.confirm(confirmMsg)) {
        return;
      }

      const payload = businessCategoryKey
        ? { businessCategoryKey, force: false }
        : {
            categoryCode: catMain.trim(),
            subcategoryCode: catSub.trim(),
            force: false,
          };

      const res = await catalogTemplateApi.apply(payload);
      if (res.skipped) {
        setCatalogMsg(res.message || 'All template lines are already in your list.');
      } else {
        setCatalogMsg(res.message || 'Template loaded.');
      }
      await loadServices();
    } catch (err) {
      const msg = err?.message || err?.error || String(err);
      setError(msg);
      setCatalogMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCatalogTemplate = async (force = false) => {
    setCatalogMsg('');
    setLoading(true);
    setError(null);
    try {
      const payload = templateBinding?.businessCategoryKey
        ? { businessCategoryKey: templateBinding.businessCategoryKey, force }
        : {
            categoryCode: catMain.trim(),
            subcategoryCode: catSub.trim(),
            force,
          };
      const res = await catalogTemplateApi.apply(payload);
      if (res.skipped) {
        setCatalogMsg(res.message || 'Already imported. Use force to add new template lines only.');
      } else {
        setCatalogMsg(res.message || 'Catalog applied.');
      }
      await loadServices();
    } catch (err) {
      const msg = err?.message || err?.error || String(err);
      setError(msg);
      setCatalogMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  const categoryOptionsForFilter = [
    ...new Set([
      ...mainCategories.map((c) => c.name),
      ...services.map((s) => s.main_category_name || s.main_category).filter(Boolean),
    ]),
  ].sort();

  return (
    <div className="services-management">
      <div className="page-header">
        <div>
          <h1>Products / Services</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, opacity: 0.85 }}>
            Shared pool with Mitra · Main category → item → ID
          </p>
        </div>
        <div className="header-actions">
          <Link to="/bills/inventory" className="btn btn-secondary">
            Inventory hub
          </Link>
          <Link to="/bills/main-categories" className="btn btn-secondary">
            Main Categories
          </Link>
          {selectedServices.size > 0 && (
            <button 
              className="btn btn-danger" 
              onClick={handleDeleteSelected}
              disabled={loading}
            >
              <i className="fas fa-trash"></i> Delete Selected ({selectedServices.size})
            </button>
          )}
          {filteredServices.length > 0 && (
            <button 
              className="btn btn-danger" 
              onClick={handleDeleteAll}
              disabled={loading}
            >
              <i className="fas fa-trash-alt"></i> Delete All
            </button>
          )}
          <button
            className="btn btn-success"
            type="button"
            disabled={loading || templateBindingLoading}
            onClick={handleLoadFromTemplate}
            title={
              templateBinding?.label
                ? `Add missing lines from default list for ${templateBinding.label}`
                : 'Add missing lines from your shop type default list'
            }
          >
            <i className="fas fa-download"></i> Load from Template
          </button>
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>
            <i className="fas fa-plus"></i> Add Product / Service
          </button>
        </div>
      </div>

      {templateBinding?.label && (
        <p className="catalog-import-hint" style={{ marginBottom: '0.75rem', fontSize: 14, color: '#334155' }}>
          Shop type: <strong>{templateBinding.label}</strong>
          {templateBindingLoading ? ' (loading…)' : ''}
        </p>
      )}
      {catalogMsg && !error && (
        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          <i className="fas fa-info-circle"></i> {catalogMsg}
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      {inventorySummary.tracked > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            marginBottom: '1rem',
            padding: '12px 16px',
            background: '#f8fafc',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            alignItems: 'center',
          }}
        >
          <strong style={{ marginRight: 8 }}>Inventory</strong>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ padding: '4px 10px', fontSize: 13 }}
            onClick={() => setStockFilter('tracked')}
          >
            {inventorySummary.tracked} tracked
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={{
              padding: '4px 10px',
              fontSize: 13,
              background: inventorySummary.low ? '#fef3c7' : undefined,
            }}
            onClick={() => setStockFilter('low')}
          >
            {inventorySummary.low} low
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={{
              padding: '4px 10px',
              fontSize: 13,
              background: inventorySummary.out ? '#fee2e2' : undefined,
            }}
            onClick={() => setStockFilter('out')}
          >
            {inventorySummary.out} out
          </button>
          {stockFilter !== 'all' && (
            <button
              type="button"
              className="btn btn-link"
              style={{ fontSize: 13 }}
              onClick={() => setStockFilter('all')}
            >
              Clear stock filter
            </button>
          )}
        </div>
      )}

      <details className="catalog-import-hint" style={{ marginBottom: '1rem', padding: '12px 16px', background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
          Import suggested catalog (optional)
        </summary>
        <p style={{ marginTop: 8, fontSize: 14, color: '#334155' }}>
          Uses your <strong>Citizen main + sub category codes</strong> (e.g. <code>general_daily_shops</code> + <code>kirana</code>).
          First import adds lines; later imports skip duplicates unless you use force. Prices are editable after import.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 8 }}>
          <input
            className="search-input"
            style={{ minWidth: 200 }}
            placeholder="Main category code"
            value={catMain}
            onChange={(e) => setCatMain(e.target.value)}
          />
          <input
            className="search-input"
            style={{ minWidth: 200 }}
            placeholder="Sub category code"
            value={catSub}
            onChange={(e) => setCatSub(e.target.value)}
          />
          <button type="button" className="btn btn-primary" disabled={loading} onClick={() => handleApplyCatalogTemplate(false)}>
            Import defaults
          </button>
          <button type="button" className="btn btn-secondary" disabled={loading} onClick={() => handleApplyCatalogTemplate(true)}>
            Import (force new lines)
          </button>
        </div>
        {catalogMsg && (
          <p style={{ marginTop: 8, fontSize: 14 }}>{catalogMsg}</p>
        )}
      </details>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search name, main category, unit, inventory… ('all' = clear)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <i className="fas fa-search"></i>
        </div>

        <select
          value={mainCategoryIdFilter}
          onChange={(e) => {
            setMainCategoryIdFilter(e.target.value);
            setCategoryFilter('all');
          }}
          className="filter-select"
        >
          <option value="all">All main categories</option>
          {mainCategories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name} ({cat.kind})
            </option>
          ))}
        </select>

        <select
          value={itemKindFilter}
          onChange={(e) => setItemKindFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">Products &amp; services</option>
          <option value="product">Products</option>
          <option value="service">Services</option>
        </select>

        <select
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setMainCategoryIdFilter('all');
          }}
          className="filter-select"
        >
          <option value="all">Category name (text)</option>
          {categoryOptionsForFilter.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>

        <select
          value={subCategoryFilter}
          onChange={(e) => setSubCategoryFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Sub Categories (optional)</option>
          {getUniqueSubCategories().map(subCat => (
            <option key={subCat} value={subCat}>{subCat}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Status</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>

        <select
          value={stockFilter}
          onChange={(e) => setStockFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All stock</option>
          <option value="tracked">Tracked only</option>
          <option value="low">Low stock</option>
          <option value="out">Out of stock</option>
        </select>

        <div className="price-range-filter">
          <input
            type="number"
            placeholder="Min Price"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
            className="price-input"
            min="0"
            step="0.01"
          />
          <span className="price-separator">-</span>
          <input
            type="number"
            placeholder="Max Price"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            className="price-input"
            min="0"
            step="0.01"
          />
        </div>

        <select
          value={`${sortBy}_${sortOrder}`}
          onChange={(e) => {
            const [field, order] = e.target.value.split('_');
            setSortBy(field);
            setSortOrder(order);
          }}
          className="filter-select"
        >
          <option value="updated_at_desc">Sort: Updated (Newest)</option>
          <option value="updated_at_asc">Sort: Updated (Oldest)</option>
          <option value="created_at_desc">Sort: Created (Newest)</option>
          <option value="created_at_asc">Sort: Created (Oldest)</option>
          <option value="name_asc">Sort: Name (A-Z)</option>
          <option value="name_desc">Sort: Name (Z-A)</option>
          <option value="price_asc">Sort: Price (Low-High)</option>
          <option value="price_desc">Sort: Price (High-Low)</option>
          <option value="category_asc">Sort: Category (A-Z)</option>
          <option value="category_desc">Sort: Category (Z-A)</option>
        </select>

        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show Inactive
        </label>
      </div>

      {loading && services.length === 0 ? (
        <div className="loading">Loading services...</div>
      ) : (
        <div className="table-container">
          <table className="services-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={filteredServices.length > 0 && selectedServices.size === filteredServices.length}
                    onChange={handleSelectAll}
                    title="Select All"
                  />
                </th>
                <th>ID</th>
                <th>Service Name</th>
                <th>Category</th>
                <th>Sub Category</th>
                <th>Price</th>
                <th>Challana</th>
                <th>Keywords</th>
                <th>Dynamic</th>
                <th>Status</th>
                <th>Stock</th>
                <th>Created At</th>
                <th>Updated At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredServices.length === 0 ? (
                <tr>
                  <td colSpan="14" className="no-data">No services found</td>
                </tr>
              ) : (
                filteredServices.map(service => {
                  const badge = stockBadge(service);
                  return (
                  <tr key={service.id} className={!service.is_active ? 'inactive' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedServices.has(service.id)}
                        onChange={() => handleSelectService(service.id)}
                      />
                    </td>
                    <td>{service.id}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {service.image_url || service.imageUrl || service.thumbnail_url ? (
                          <img
                            src={service.image_url || service.imageUrl || service.thumbnail_url}
                            alt=""
                            style={{
                              width: 40,
                              height: 40,
                              objectFit: 'cover',
                              borderRadius: 6,
                              border: '1px solid #e2e8f0',
                              background: '#f8fafc',
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <span
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: 6,
                              background: '#f1f5f9',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#94a3b8',
                              fontSize: 11,
                            }}
                          >
                            —
                          </span>
                        )}
                        <span>{service.name}</span>
                      </div>
                    </td>
                    <td>
                      {service.main_category_name || service.main_category || 'Other (miscellaneous)'}
                      {service.item_kind || service.main_category_kind ? (
                        <div style={{ fontSize: 11, opacity: 0.7 }}>
                          {service.item_kind || service.main_category_kind}
                          {service.default_unit || service.main_category_unit
                            ? ` · ${service.default_unit || service.main_category_unit}`
                            : ''}
                        </div>
                      ) : null}
                    </td>
                    <td>{service.sub_category}</td>
                    <td>₹{parseFloat(service.price || 0).toFixed(2)}</td>
                    <td>₹{parseFloat(service.challana || 0).toFixed(2)}</td>
                    <td className="keywords-cell">
                      {Array.isArray(service.keywords) 
                        ? service.keywords.join(', ') 
                        : (service.keywords || '-')}
                    </td>
                    <td>
                      {service.has_dynamic_sub_service && (
                        <span className="badge badge-info">Sub Service</span>
                      )}
                      {service.has_dynamic_price && (
                        <span className="badge badge-warning">Price</span>
                      )}
                      {!service.has_dynamic_sub_service && !service.has_dynamic_price && '-'}
                    </td>
                    <td>
                      <span className={`badge ${service.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {service.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {badge ? (
                        <span className={`badge ${badge.className}`}>{badge.label}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="date-time-cell">
                      {formatDateTime(service.created_at)}
                    </td>
                    <td className="date-time-cell">
                      {formatDateTime(service.updated_at || service.created_at)}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleOpenModal(service)}
                          title="Edit"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        {service.is_active ? (
                          <button
                            className="btn-icon btn-deactivate"
                            onClick={() => handleDelete(service.id, false)}
                            title="Deactivate"
                          >
                            <i className="fas fa-eye-slash"></i>
                          </button>
                        ) : (
                          <>
                            <button
                              className="btn-icon btn-activate"
                              onClick={() => handleOpenModal({ ...service, is_active: true })}
                              title="Activate"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDelete(service.id, true)}
                              title="Delete Permanently"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          className="modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseModal();
            }
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>{editingService ? 'Edit Product / Service' : 'Add Product / Service'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="service-form">
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#475569' }}>
                Order: <strong>1. Main category</strong> → <strong>2. Name</strong> →{' '}
                <strong>3. ID</strong> → rest.{' '}
                <Link to="/bills/main-categories" target="_blank" rel="noreferrer">
                  Manage main categories
                </Link>
              </p>

              <div className="form-group">
                <label>1. Main Category *</label>
                <select
                  name="main_category_id"
                  value={formData.main_category_id}
                  onChange={handleMainCategoryChange}
                  required
                >
                  <option value="">Select main category</option>
                  {mainCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name} · {cat.kind}
                      {cat.default_unit ? ` · ${cat.default_unit}` : ''}
                    </option>
                  ))}
                </select>
                {!mainCategories.length && (
                  <small style={{ color: '#b91c1c' }}>
                    No categories yet — create one under Main Categories first.
                  </small>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>2. Product / Service Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder={
                      formData.item_kind === 'product'
                        ? 'e.g., Santoor'
                        : 'e.g., Color Print'
                    }
                  />
                </div>

                <div className="form-group">
                  <label>3. ID (optional)</label>
                  <input
                    type="number"
                    name="id"
                    value={formData.id}
                    onChange={handleInputChange}
                    disabled={!!editingService}
                    placeholder="Auto-generated if empty"
                    min="1"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select
                    name="item_kind"
                    value={formData.item_kind}
                    onChange={(e) => {
                      const kind = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        item_kind: kind,
                        track_inventory: kind === 'product',
                      }));
                    }}
                  >
                    <option value="product">Product</option>
                    <option value="service">Service</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Unit</label>
                  <input
                    type="text"
                    name="default_unit"
                    value={formData.default_unit}
                    onChange={handleInputChange}
                    placeholder="piece / ltr / …"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Sub Category (optional)</label>
                <input
                  type="text"
                  name="sub_category"
                  value={formData.sub_category}
                  onChange={handleInputChange}
                  placeholder="Optional label"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Price (₹) *</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label>GST % (optional)</label>
                  <input
                    type="number"
                    name="gst_percent"
                    value={formData.gst_percent}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="e.g. 5, 12, 18"
                  />
                  <small>Used on the final bill as Total GST (price × GST % × qty).</small>
                </div>

                <div className="form-group">
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '500' }}>
                      <input
                        type="checkbox"
                        checked={formData.has_challana}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            has_challana: e.target.checked,
                            challana: e.target.checked ? (prev.challana || '0') : ''
                          }));
                        }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      Has Challana Amount?
                    </label>
                  </div>
                  {formData.has_challana && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
                        Challana Amount (₹) *
                      </label>
                      <input
                        type="number"
                        name="challana"
                        value={formData.challana}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                      <small style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '12px' }}>
                        Board/Government challana amount (e.g., TET 2025 application fee)
                      </small>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Keywords (comma-separated)</label>
                <input
                  type="text"
                  name="keywords"
                  value={formData.keywords}
                  onChange={handleInputChange}
                  placeholder="online, application, ssc, tet"
                />
                <small>Separate keywords with commas</small>
              </div>

              <div className="form-group">
                <label>Details (for AI image prompt)</label>
                <textarea
                  name="product_details"
                  value={formData.product_details || ''}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="Short description used for AI thumbnail prompt…"
                />
              </div>

              <ProductImagePicker
                value={formData.image_url || ''}
                onChange={(url) => setFormData((prev) => ({ ...prev, image_url: url || '' }))}
                name={formData.name}
                details={formData.product_details}
                itemKind={formData.item_kind || 'product'}
              />

              <div className="form-row">
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="has_dynamic_sub_service"
                      checked={formData.has_dynamic_sub_service}
                      onChange={handleInputChange}
                    />
                    Has Dynamic Sub Service
                  </label>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="has_dynamic_price"
                      checked={formData.has_dynamic_price}
                      onChange={handleInputChange}
                    />
                    Has Dynamic Price
                  </label>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                    Active
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="track_inventory"
                      checked={formData.track_inventory}
                      onChange={handleInputChange}
                    />
                    Track inventory (product)
                  </label>
                  <small style={{ display: 'block', color: '#666', fontSize: '12px' }}>
                    Sales on Smart Bill will auto-reduce available qty. Services stay untracked.
                  </small>
                </div>
              </div>

              {formData.track_inventory && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Available qty</label>
                    <input
                      type="number"
                      name="available_qty"
                      min="0"
                      step="1"
                      value={formData.available_qty}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Low-stock alert at</label>
                    <input
                      type="number"
                      name="low_stock_threshold"
                      min="0"
                      step="1"
                      value={formData.low_stock_threshold}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              )}

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : (editingService ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesManagement;

